/**
 * URL → 二维码图片组件（IKBFM0 服务号扫码真接）。
 *
 * 服务号网页授权 URL 无法在 PC 浏览器内嵌展示（微信限制「请在微信客户端打开链接」），
 * 由本组件在本地把授权 URL 渲染为二维码图，员工用手机微信「扫一扫」完成授权。
 * qrcode 库纯前端本地编码，不请求任何外部服务。
 */
import { useEffect, useState } from 'react';
import QRCode from 'qrcode';

export interface QrcodeImageProps {
  /** 编码进二维码的内容（如服务号网页授权 URL）。 */
  url: string;
  /** 二维码边长（px）。 */
  size?: number;
  /** 暗色模块色（默认跟随品牌绿）。 */
  color?: string;
}

/** URL 变化即重新编码；编码失败渲染占位框（不抛错打断登录页）。 */
export function QrcodeImage({ url, size = 220, color = '#0f6b5c' }: QrcodeImageProps) {
  const [dataUrl, setDataUrl] = useState<string>('');
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(false);
    QRCode.toDataURL(url, {
      width: size,
      margin: 2,
      errorCorrectionLevel: 'M',
      color: { dark: color, light: '#ffffff' },
    })
      .then((result) => {
        if (!cancelled) setDataUrl(result);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [url, size, color]);

  if (error) {
    return (
      <div
        data-testid="qrcode-image-error"
        style={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px dashed #d9d9d9',
          borderRadius: 8,
          color: '#999',
          fontSize: 12,
          padding: 12,
          textAlign: 'center',
          boxSizing: 'border-box',
        }}
      >
        二维码生成失败，请刷新重试
      </div>
    );
  }

  return (
    <img
      data-testid="qrcode-image"
      src={dataUrl || undefined}
      alt="微信扫码二维码"
      width={size}
      height={size}
      style={{ display: 'block', opacity: dataUrl ? 1 : 0, transition: 'opacity .2s' }}
    />
  );
}
